import React, { useEffect, useContext, useState, useRef, useMemo } from 'react';
import Select from 'react-select';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { SlideDown } from 'react-slidedown'
import { Button } from 'react-bootstrap';
import { FormModal, PersonalMessageForm, SmallModal } from 'sub-components';
import { UserProfileVideoOverlayed, SmallPersonCard } from 'components';
import AuthContext from 'app/AuthContext';
import classes from './DiscoveryPage.module.scss';
import { getUserRecommended, getDefaultUserCommunities, updateWatchedVideos, getUsersInCommunity, getUpcomingEvents, updateCommunityAffil, getCommunityByName, updateEventAttendance } from 'lib/user';
import Share06 from "@untitled-ui/icons-react/build/cjs/Share06";
import Mail01 from "@untitled-ui/icons-react/build/cjs/Mail01";
import Home02 from "@untitled-ui/icons-react/build/cjs/Home02";
import Globe05 from "@untitled-ui/icons-react/build/cjs/Globe05";
import ArrowCircleLeft from "@untitled-ui/icons-react/build/cjs/ArrowCircleLeft";
import ChevronDown from "@untitled-ui/icons-react/build/cjs/ChevronDown";
import FaceFrown from "@untitled-ui/icons-react/build/cjs/FaceFrown";
import FaceHappy from "@untitled-ui/icons-react/build/cjs/FaceHappy";
import Telescope from "@untitled-ui/icons-react/build/cjs/Telescope";
import { v4 as uuidv4 } from 'uuid';

const DiscoveryPage = () => {
    const { authToken, userMetadata } = useContext(AuthContext);
    const navigate = useNavigate();
    const loadingTimeoutRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [startIndex, setStartIndex] = useState(0);
    const [isSignUpToggleOn, setIsSignUpToggleOn] = useState(false);
    const [isSignInToggleOn, setIsSignInToggleOn] = useState(false);
    const [actionCompleted, setActionCompleted] = useState(false);
    const [timeoutId, setTimeoutId] = useState(null);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [direction, setDirection] = useState(1); // 1 for down, -1 for up
    const containerRef = useRef(null);
    const videoRefs = useRef([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [playingStates, setPlayingStates] = useState(() =>
        users.map((_, index) => index === 0 ? false : false)
    );

    // Messaging system
    const [isApplicationToggleOn, setApplicationToggleOn] = useState(false);
    const [messageTargetUser, setMessageTargetUser] = useState(null);

    // Notification system
    const [notificationVisible, setNotificationVisible] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    // Manages which videos were watched
    const [watchedVideos, setWatchedVideos] = useState(new Set());
    const watchedVideosRef = useRef(new Set());

    // Tab/Filter management
    const { tab } = useParams(); // "explore", "communities", etc.
    const [searchParams] = useSearchParams();
    const communityName = searchParams.get('name'); // For communities dropdown
    const [isCommunitiesClosed, setIsCommunitiesClosed] = useState(true);
    const [isCommunityTopMenuClosed, setIsCommunityTopMenuClosed] = useState(true);
    const defaultCommunities = getDefaultUserCommunities();

    // Community management
    const [currentCommunity, setCurrentCommunity] = useState(null);
    const [isMemberOfCommunity, setIsMemberOfCommunity] = useState(false);
    const [joinedCommunityModal, setJoinedCommunityModal] = useState(false);
    const [leftCommunityModal, setLeftCommunityModal] = useState(false);
    const [joinedEventModal, setJoinedEventModal] = useState(false);
    const [leftEventModal, setLeftEventModal] = useState(false);

    // Selected profile system
    const [selectedCardIndex, setSelectedCardIndex] = useState(0);
    // Title filter
    const [selectedTitle, setSelectedTitle] = useState(null);
    const [titleOptions, setTitleOptions] = useState([]);
    
    // Event management
    const upcomingEvents = useMemo(() => {
        return getUpcomingEvents([currentCommunity?.icon]);
    }, [currentCommunity?.icon]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isUserInEventAttendance, setUserInEventAttendance] = useState(false);

    const toggleCommunities = () => {
        setIsCommunitiesClosed(!isCommunitiesClosed);
    };

    const toggleCommunityTopMenu = () => {
        setIsCommunityTopMenuClosed(!isCommunityTopMenuClosed);
    };

    // useEffect(() => {
    //     if (!authToken) {
    //         if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("No auth token found. Please login."); }
    //         // handleSignUpClick();
    //         handleSignInClick();
    //     }
    // }, [authToken]); // useEffect will run whenever authToken changes

    const handleSignUpClick = () => {
        setIsSignUpToggleOn(!isSignUpToggleOn);
        setActionCompleted(false);
    };

    const handleFilterSelect = (action, value) => {
        setIsCommunityTopMenuClosed(true)
        if (action == 'title') {
            setSelectedTitle(value);
        }
        else if (action == 'event') {
            setSelectedEvent(value);
        }
    }

    const handleSignInClick = () => {
        setIsSignInToggleOn(!isSignInToggleOn);
        setActionCompleted(false);
    };

    const handleActionCompleted = () => {
        setActionCompleted(true);
        setIsSignUpToggleOn(false);
        setIsSignInToggleOn(false);
    };

    const checkIfLoggedIn = () => {
        if (authToken) {
            clearTimeout(timeoutId);
        } else {
            setIsSignUpToggleOn(false);
            setIsSignInToggleOn(false);
        }
    };

    // Snapping logic
    const handleScroll = () => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const videoHeight = container.clientHeight;
        const scrollTop = container.scrollTop;

        // Calculate the index of the closest video
        const nextIndex = Math.round(scrollTop / videoHeight);

        // If index changes, update and snap
        if (nextIndex !== currentVideoIndex) {
            setCurrentVideoIndex(nextIndex);
            setSelectedCardIndex(nextIndex);
            container.scrollTo({
                top: nextIndex * videoHeight,
                behavior: "smooth",
            });
        }
    };

    const removeFilter = (filter) => {
        if (filter === 'title') {
            setSelectedTitle(null);
        }
        else if (filter === 'event') {
            setSelectedEvent(null);
        }
    }

    // Shows attendees when path includes community and screen width is smaller than desktop
    const [showAttendees, setShowAttendees] = useState(window.innerWidth < 1400);
    const [isMobileVideoVisible, setIsMobileVideoVisible] = useState(false);

    // Handles the selection of a profile card
    const handleCardSelect = (index) => {
        setSelectedCardIndex(index);
        setCurrentVideoIndex(index);
        const videoHeight = containerRef.current?.clientHeight || 0;
        containerRef.current?.scrollTo({
            top: index * videoHeight,
            behavior: 'smooth'
        });

        // Show video section on small screens
        if (window.innerWidth < 600) {
            setIsMobileVideoVisible(true);
        }
    };


    // Helper to add a video to the watched list
    const addWatchedVideo = (videoId) => {
        if (!watchedVideosRef.current.has(videoId)) {
            watchedVideosRef.current.add(videoId); // Add to ref
            setWatchedVideos((prev) => {
                const updated = new Set(prev);
                updated.add(videoId);
                return updated;
            });
        }
    };

    // Share video function copies the profile link to the clipboard
    const shareVideo = (user) => {
        let profileLink = process.env.REACT_APP_ENVIRONMENT === "dev" ? "http://localhost:3000" : "https://www.sidejawn.io";
        if (user?.userId) {
            profileLink = profileLink + '/profile/' + user?.userId;
        }
        if (user?.displayName) {
            setNotificationMessage(`${user.displayName}'s link copied to clipboard!`);
        }
        // Check if navigator.clipboard is available
        if (navigator.clipboard) {
            navigator.clipboard.writeText(profileLink).then(() => {
                setNotificationVisible(true); // Show notification
                setTimeout(() => {
                    setNotificationVisible(false); // Hide notification after 2 seconds
                }, 2000);
            }).catch(err => {
                console.error('Failed to copy: ', err);
            });
        } else {
            // Fallback method
            const textarea = document.createElement('textarea');
            textarea.value = profileLink;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy'); // Attempt to copy
                setNotificationVisible(true); // Show notification
                setTimeout(() => {
                    setNotificationVisible(false); // Hide notification after 2 seconds
                }, 2000);
            } catch (err) {
                console.error('Fallback: Unable to copy: ', err);
            }
            document.body.removeChild(textarea); // Clean up
        }
    };

    const messagePerson = (user) => {
        if (authToken) {
            setMessageTargetUser(user);
            openApplicationForm();
        } else {
            handleSignInClick();
        }
    };

    const openApplicationForm = () => {
        setApplicationToggleOn(!isApplicationToggleOn);
        setActionCompleted(false);
    };

    const closeApplicationForm = () => {
        setApplicationToggleOn(false);
    }

    const handleApplyCompleted = () => {
        setActionCompleted(true);
    };

    const handleCreateClick = () => {
        navigate(`/profile/${userMetadata?.id}/video/edit`);
    }

    const handleProfileClick = () => {
        navigate(`/profile/${userMetadata?.id}`);
    }

    const loadUserVideos = async () => {
        // TODO: Create better way to track a guest user
        const filters = {
            filters: {
                ...(communityName ? { community: communityName } : {}),
                ...(tab === 'explore' && {
                    watched: false,
                    emailed: false
                }),
                // Explore batches are limited to 3 people. Communities have less of a limit for event purposes.
                maxBatch: tab == 'community' ? 10 : 5,
                // Explore batches are released when 5 people are available.
                minBatch: tab == 'community' ? 0 : 0,
            }
        };

        const { isSuccess: loadVideosSuccess, resp: recommendedVideosResp, err: loadVideosError } = await getUserRecommended(userMetadata?.id ? userMetadata.id : 'guest-id', 'videos=true', filters, authToken ? authToken : 'guest-auth');
        if (loadVideosSuccess) {
            if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("User Video Recs Loading || Success", recommendedVideosResp); }
            const extendedVideos = [
                ...(recommendedVideosResp.results ? recommendedVideosResp.results : []),
                { id: uuidv4(), isPlaceholder: true, profileMetadata: {} }, // Add a placeholder at the end
            ];
            setUsers(extendedVideos);
            // Title filter options
            const titlesSet = new Set();
            (recommendedVideosResp.results || []).forEach(video => {
                const title = video.profileMetadata?.title;
                if (title) titlesSet.add(title);
            });
            const options = Array.from(titlesSet).map(title => ({ label: title, value: title }));
            setTitleOptions(options);
        }
        else {
            if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("Error loading user video recs", loadVideosError); }
        }
    };

    const loadUsersInCommunity = async () => {
        const { isSuccess: loadUsersSuccess, resp: usersInCommunityResp, err: loadUsersError } = await getUsersInCommunity(
            currentCommunity?.id,
            null,
            authToken,
            undefined,
            0,  // skip
            0,  // limit
            userMetadata?.id  // loggedInUserId
        );
        const placeholder = {isPlaceholder: true, profileMetadata: {userId: uuidv4()}, media: {} };
        if (loadUsersSuccess) {
            if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("Users in community loading || Success", usersInCommunityResp); }
            const extendedVideos = [
                ...(usersInCommunityResp.results ? usersInCommunityResp.results : []),
                placeholder, // Add a placeholder at the end
            ];
            setUsers(extendedVideos);
            setFilteredUsers(extendedVideos);

            // Displays relationship between current user and community
            const userIds = (usersInCommunityResp.results || []).map(user => user.userId);
            const isUserMember = userIds.includes(userMetadata?.id);
            setIsMemberOfCommunity(isUserMember);

            // Title filter options
            const titlesSet = new Set();
            (usersInCommunityResp.results || []).forEach(user => {
                const title = user.profileMetadata?.title;
                if (title) titlesSet.add(title);
            });
            const options = Array.from(titlesSet).map(title => ({ label: title, value: title }));
            setTitleOptions(options);
        }
        else {
            if (usersInCommunityResp.http_code == 404) {
                setUsers([]);
                setIsMemberOfCommunity(false);
            }
            else if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("Error loading users in community", loadUsersError); }
        }
    }

    // Join or leave community
    const joinLeaveComm = async (updateAction) => {
        if (!userMetadata?.id) {
            console.error('User ID not available.');
            return;
        }
        try {
            const { isSuccess, resp, err } = await updateCommunityAffil(userMetadata.id, currentCommunity.id, updateAction, authToken);
            if (isSuccess) {
                console.log('Successfully joined community:', resp);
                if (updateAction == 'leave' && users[0].userId == userMetadata.id) {
                    setUsers(prev => prev.slice(1));
                    setFilteredUsers(prev => prev.slice(1));
                    setIsMemberOfCommunity(false);
                    setLeftCommunityModal(true);
                }
                // Insert the new userVideo object to the front of both arrays if joining
                else if (updateAction == 'join' && resp?.results) {
                    const newUserVideo = resp?.results;
                    setUsers(prev => [newUserVideo, ...prev]);
                    setFilteredUsers(prev => [newUserVideo, ...prev]);
                    setIsMemberOfCommunity(true);
                    setJoinedCommunityModal(true);
                }
                setIsMobileVideoVisible(false);
                setTimeout(() => {
                    handleCardSelect(0);
                }, 1000);
            } else {
                console.error('Failed to join community:', err || resp);
            }
        } catch (error) {
            console.error('Unexpected error joining community:', error);
        }
    };

    // Join or leave a community event
    const joinLeaveEvent = async (actionType) => {
        if (!userMetadata?.id) {
            console.error('User ID not available.');
            return;
        }
    
        try {
            const { isSuccess, resp, err } = await updateEventAttendance(
                userMetadata.id,
                currentCommunity.id,
                selectedEvent.id,
                actionType,
                authToken
            );
    
            if (isSuccess) {
                console.log(`Successfully ${actionType === 'join' ? 'joined' : 'left'} event:`, resp);
    
                // Update the event list for the current user's video
                const updateVideos = (videos) => {
                    return videos.map((video) => {
                        if (video.userId === userMetadata.id) {
                            let updatedEvents = video.events || [];
                            if (actionType === 'join') {
                                // Only add if not already in list
                                const alreadyIn = updatedEvents.some(e => e.id === selectedEvent.id);
                                if (!alreadyIn) {
                                    updatedEvents = [...updatedEvents, selectedEvent];
                                }
                            } else if (actionType === 'leave') {
                                updatedEvents = updatedEvents.filter(e => e.id !== selectedEvent.id);
                            }
    
                            return {
                                ...video,
                                events: updatedEvents,
                            };
                        }
                        return video;
                    });
                };
    
                const updatedVideos = updateVideos(users);
                setUsers(updatedVideos);
                setFilteredUsers(updatedVideos);
                setUserInEventAttendance(actionType === 'join');
                if (actionType === 'join') {
                    setJoinedEventModal(true);
                } else if (actionType === 'leave') {
                    setLeftEventModal(true);
                }
            } else {
                console.error(`Failed to ${actionType} event:`, err || resp);
            }
        } catch (error) {
            console.error('Unexpected error updating event attendance:', error);
        }
    };

    const handleCommunityChange = (path, param) => {
        let params = '';
        if (param) {
            params = new URLSearchParams();
            params.set("name", param);
        }
        navigate(`/discovery/${path}?${params.toString()}`);
    };

    /* The `useEffect` hook is used to perform side effects in a functional component. In this case, the
   `useEffect` hook is used to load projects and show a loading spinner if the projects haven't
   loaded after 2 seconds. */
    useEffect(() => {
        // Only shows the loading spinner if the projects haven't loaded after 2 seconds
        loadingTimeoutRef.current = setTimeout(() => {
            setIsLoading(true);
        }, 2000);
        // loadUserVideos(startIndex);
        loadUsersInCommunity();
    }, [userMetadata, currentCommunity]);

    useEffect(() => {
        // When user videos data changes, set loading to false
        clearTimeout(loadingTimeoutRef.current);
        setIsLoading(false);
    }, [users]);

    // Filter user videos based on selectedTitle, selectedEvent and prioritize current user
    useEffect(() => {
        let updatedVideos = users.filter(video => !video.isPlaceholder); // filter only real videos
    
        if (selectedTitle) {
            updatedVideos = updatedVideos.filter(
                (video) => video.profileMetadata?.title === selectedTitle.value
            );
        }

        if (selectedEvent) {
            updatedVideos = updatedVideos.filter((video) =>
                (video.events || []).some((event) => event.id === selectedEvent.id)
            );
        }

        if (userMetadata?.id) {
            const matchIndex = updatedVideos.findIndex(
                video => video.userId === userMetadata.id
            );
            if (matchIndex >= 0) {
                setUserInEventAttendance(true);
            }
            else {
                setUserInEventAttendance(false);
            }
            if (matchIndex > 0) {
                const [matchedVideo] = updatedVideos.splice(matchIndex, 1);
                updatedVideos.unshift(matchedVideo);
            }
        }
    
        // Always keep the placeholder at the end if it exists
        const placeholder = users.find(v => v.isPlaceholder);
        if (placeholder) {
            updatedVideos.push(placeholder);
        }
        setFilteredUsers(updatedVideos);
    }, [selectedTitle, selectedEvent, users]);

    // Leverages the observer to know when to automatically play videos that come into view, and keeps track of videos that are watched
    useEffect(() => {
        const observerOptions = {
            root: containerRef.current,
            rootMargin: '0px',
            threshold: 0.9,
        };

        const observer = new IntersectionObserver((entries) => {
            setPlayingStates((prevStates) => {
                const newStates = [...prevStates];
                let isPlaceholderVisible = false;

                entries.forEach((entry) => {
                    const index = parseInt(entry.target.getAttribute('data-index'), 10);
                    const video = filteredUsers[index];

                    if (video?.isPlaceholder && entry.isIntersecting) {
                        isPlaceholderVisible = true;
                    } else if (video && !video?.isPlaceholder) {
                        if (entry.isIntersecting) {
                            if (!watchedVideosRef.current.has(video.media.videoId)) {
                                addWatchedVideo(video.media.videoId); // Add to watched videos
                                updateWatchedVideos(userMetadata.id, [video.media.videoId], authToken); // Trigger API call
                            }
                            setSelectedCardIndex(index);
                        }
                        newStates[index] = entry.isIntersecting;
                    }
                });

                if (isPlaceholderVisible) {
                    for (let i = 0; i < newStates.length; i++) {
                        newStates[i] = false;
                    }
                }

                return newStates;
            });
        }, observerOptions);

        // Observe all video wrappers
        videoRefs.current.forEach((videoWrapper, index) => {
            if (filteredUsers[index]?.isPlaceholder) return; // Skip placeholder
            if (videoWrapper) observer.observe(videoWrapper);
        });

        return () => {
            videoRefs.current.forEach((videoWrapper) => {
                if (videoWrapper) observer.unobserve(videoWrapper);
            });
        };
    }, [filteredUsers]);

    // useEffect(() => {
    //     const container = containerRef.current;
    //     if (container) {
    //         container.addEventListener("scroll", handleScroll, { passive: false });
    //     }
    //     return () => {
    //         if (container) {
    //             container.removeEventListener("scroll", handleScroll);
    //         }
    //     };
    // }, []); // remove currentVideoIndex dependency

    useEffect(() => {
        const handleResize = () => {
            const isSmallScreen = window.innerWidth < 1100;
            setShowAttendees(isSmallScreen || window.innerWidth > 1400); // optional logic
            if (!isSmallScreen) {
                setIsMobileVideoVisible(false); // Reset if user grows window
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // run once on mount

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Ensure the container snaps to the current video index on mount or update
        if (containerRef.current) {
            const videoHeight = containerRef.current.clientHeight;
            containerRef.current.scrollTo({
                top: currentVideoIndex * videoHeight,
                behavior: "instant",
            });
        }
    }, [currentVideoIndex]);

    // Updates current community on page by path name if it exists
    useEffect(() => {
        if (communityName) {
            const communityInfo = getCommunityByName(communityName);
            setCurrentCommunity(communityInfo);
        }
    }, [communityName]);

    return (
        <div className={classes.discoveryContainer}>
            {joinedCommunityModal && (
                <SmallModal
                    setDisplayModal={setJoinedCommunityModal}
                    display={joinedCommunityModal}
                    hideCloseBtn={true}
                >
                    <div className={classes.successMessage}>
                        <h3>Joined {currentCommunity.label}!</h3>
                        <p>Find yourself in the list!</p>
                        <FaceHappy className={classes.icon} />
                    </div>
                </SmallModal>
            )}
            {leftCommunityModal && (
                <SmallModal
                    setDisplayModal={setLeftCommunityModal}
                    display={leftCommunityModal}
                    hideCloseBtn={true}
                >
                    <div className={classes.successMessage}>
                        <h3>Left {currentCommunity.label}.</h3>
                        <p>You will be missed.</p>
                        <FaceFrown className={classes.icon} />
                    </div>
                </SmallModal>
            )}
            {joinedEventModal && (
                <SmallModal
                    setDisplayModal={setJoinedEventModal}
                    display={joinedEventModal}
                    hideCloseBtn={true}
                >
                    <div className={classes.successMessage}>
                        <h3>Joined {selectedEvent?.label}!</h3>
                        <p>You're on the list. Get excited!</p>
                        <FaceHappy className={classes.icon} />
                    </div>
                </SmallModal>
            )}
            {leftEventModal && (
                <SmallModal
                    setDisplayModal={setLeftEventModal}
                    display={leftEventModal}
                    hideCloseBtn={true}
                >
                    <div className={classes.successMessage}>
                        <h3>Left {selectedEvent?.label}.</h3>
                        <p>Maybe next time.</p>
                        <FaceFrown className={classes.icon} />
                    </div>
                </SmallModal>
            )}
            <FormModal
                isToggleOn={isApplicationToggleOn}
                handleClick={openApplicationForm}
                onClose={() => clearTimeout(timeoutId)}
                actionCompleted={actionCompleted}
                title={`Message ${messageTargetUser?.displayName}`}
                successfulActionTitle="Message Sent!"
                successfulActionMessage={"Check your email for next steps, and keep exploring! \n (Double check your junk mail just in case.)"}>
                <PersonalMessageForm
                    onActionCompleted={handleApplyCompleted}
                    receiverId={messageTargetUser}
                    handleCloseModal={closeApplicationForm}
                />
            </FormModal>
            <div className={`${classes.topMenu} 
              ${isMobileVideoVisible ? classes.topMenuTransparent : ''} 
              ${isMobileVideoVisible && !isCommunityTopMenuClosed ? classes.topMenuWithOverlay : ''}`}>
                <div>
                    <div className={classes.communityHeaderContainer}>
                        <div className={classes.communityHeader} onClick={toggleCommunityTopMenu}>
                            {window.innerWidth < 1100 && (
                                isMobileVideoVisible ? (
                                    <ArrowCircleLeft
                                        className={classes.backButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsMobileVideoVisible(false);
                                        }}
                                    />
                                ) : (
                                    <img className={classes.communityIcon} src={`/community-icons/${currentCommunity?.icon}.png`} alt={currentCommunity?.icon} />
                                )
                            )}
                            <div>{currentCommunity?.label || '(Missing community)'}</div>
                            <ChevronDown className={`${classes.downIcon} ${!isCommunityTopMenuClosed ? classes.rotated : ''}`} />
                        </div>
                        {!isMemberOfCommunity &&
                            <div className={classes.joinCommunity} onClick={() => joinLeaveComm('join')}>
                                Join
                            </div>
                        }
                    </div>
                    <div className={`${classes.collapsableContainer} ${!isCommunityTopMenuClosed ? classes.collapsableOpen : ''}`}>
                        <div className={classes.leaveCommunity} onClick={() => joinLeaveComm('leave')}>
                            Leave community
                        </div>
                        <div className={classes.externalLinks}>
                            {currentCommunity?.externalLinks?.map((link, index) => {
                                const label = link.label.toLowerCase();
                                const url = link.link;

                                return (
                                    <div
                                        key={index}
                                        className={classes.externalLink}
                                        onClick={() => window.open(url, '_blank')}
                                        style={{ backgroundImage: `url(/icons/${label}.png)` }}
                                    />
                                );
                            })}
                        </div>
                        <div className={classes.description}>
                            <h3>Description</h3>
                            <p>
                                Most of our community has full-time jobs and startups on the side. We love to build so let's actually build something and have a raving conversation about the latest developments in AI!
                            </p>
                        </div>
                        <h3>Filters</h3>
                        <div className={classes.filters}>
                            <div className={classes.filterContainer}>
                                {selectedTitle && (
                                    <div
                                        className={classes.smallDeleteButton}
                                        onClick={() => removeFilter('title')}
                                    />
                                )}
                                <Select
                                    value={selectedTitle}
                                    onChange={(value) => handleFilterSelect('title', value)}
                                    placeholder="Filter by title"
                                    options={titleOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    menuPlacement={'auto'}
                                    isSearchable={false}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (provided) => ({
                                            ...provided,
                                            borderColor: '#edecec',
                                            borderRadius: '0.75em',
                                            fontSize: '16px',
                                            minWidth: '180px',
                                            backgroundColor: 'transparent',
                                            height: '30px',
                                            alignContent: 'center',
                                            flexWrap: 'nowrap'
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: 'white' // Controls placeholder text color
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }),
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        })
                                    }}
                                />
                            </div>
                            <div className={classes.filterContainer}>
                                {selectedEvent && (
                                    <div
                                        className={classes.smallDeleteButton}
                                        onClick={() => removeFilter('event')}
                                    />
                                )}
                                <Select
                                    value={selectedEvent}
                                    onChange={(value) => handleFilterSelect('event', value)}
                                    placeholder="Upcoming events"
                                    options={upcomingEvents}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    menuPlacement={'auto'}
                                    isSearchable={false}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (provided) => ({
                                            ...provided,
                                            borderColor: '#edecec',
                                            borderRadius: '0.75em',
                                            fontSize: '16px',
                                            minWidth: '180px',
                                            backgroundColor: 'transparent',
                                            height: '30px',
                                            alignContent: 'center',
                                            flexWrap: 'nowrap'
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: 'white' // Controls placeholder text color
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }),
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.filters}>
                    <div className={classes.filterContainer}>
                        {
                            selectedTitle && isCommunityTopMenuClosed &&
                            <>
                                {selectedTitle && (
                                    <div
                                        className={classes.smallDeleteButton}
                                        onClick={() => removeFilter('title')}
                                    />
                                )}
                                <Select
                                    value={selectedTitle}
                                    onChange={(value) => handleFilterSelect('title', value)}
                                    placeholder="Filter by title"
                                    options={titleOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    menuPlacement={'auto'}
                                    isSearchable={false}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (provided) => ({
                                            ...provided,
                                            borderColor: '#edecec',
                                            borderRadius: '0.75em',
                                            fontSize: '16px',
                                            minWidth: '180px',
                                            backgroundColor: 'transparent',
                                            height: '30px',
                                            alignContent: 'center',
                                            flexWrap: 'nowrap'
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: 'white' // Controls placeholder text color
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }),
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        })
                                    }}
                                />
                            </>
                        }
                    </div>
                    <div className={classes.filterContainer}>
                        {
                            selectedEvent && isCommunityTopMenuClosed &&
                            <>
                                {selectedEvent && (
                                    <div
                                        className={classes.smallDeleteButton}
                                        onClick={() => removeFilter('event')}
                                    />
                                )}
                                <Select
                                    value={selectedEvent}
                                    onChange={(value) => handleFilterSelect('event', value)}
                                    placeholder="Upcoming events"
                                    options={upcomingEvents}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    menuPlacement={'auto'}
                                    isSearchable={false}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (provided) => ({
                                            ...provided,
                                            borderColor: '#edecec',
                                            borderRadius: '0.75em',
                                            fontSize: '16px',
                                            minWidth: '180px',
                                            backgroundColor: 'transparent',
                                            height: '30px',
                                            alignContent: 'center',
                                            flexWrap: 'nowrap'
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: 'white' // Controls placeholder text color
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }),
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        })
                                    }}
                                />
                            </>
                        }
                    </div>
                </div>
            </div>
            <div className={classes.headerSection}>
                <div>
                    <div className={classes.communityHeaderContainer}>
                        <div className={classes.communityHeader} onClick={toggleCommunityTopMenu}>
                            <img className={classes.communityIcon} src={`/community-icons/${currentCommunity?.icon}.png`} alt={currentCommunity?.icon} />
                            <div>
                                <div className={classes.communityName}>{currentCommunity?.label || '(Missing community)'}</div>
                                {isMemberOfCommunity ? (
                                    <div className={classes.leaveCommunity} onClick={() => joinLeaveComm('leave')}>
                                        - Leave community
                                    </div>
                                ) : (
                                    <div className={classes.joinCommunity} onClick={() => joinLeaveComm('join')}>
                                        + Join community
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={classes.externalLinks}>
                        {currentCommunity?.externalLinks?.map((link, index) => {
                            const label = link.label.toLowerCase();
                            const url = link.link;

                            return (
                                <div
                                    key={index}
                                    className={classes.externalLink}
                                    onClick={() => window.open(url, '_blank')}
                                    style={{ backgroundImage: `url(/icons/${label}.png)` }}
                                />
                            );
                        })}
                    </div>
                    <div className={classes.description}>
                        <h3>Description</h3>
                        <p>
                            {currentCommunity?.description}
                        </p>
                    </div>
                    <h3>Filter {filteredUsers.length - 1} people by</h3>
                </div>
                <div className={classes.filters}>
                    <div className={classes.filterContainer}>
                        <Select
                            value={selectedTitle}
                            onChange={setSelectedTitle}
                            placeholder="Occupation"
                            options={titleOptions}
                            isCreatable={true}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            menuPlacement={'auto'}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                control: (provided) => ({
                                    ...provided,
                                    borderColor: '#edecec',
                                    borderRadius: '0.75em',
                                    fontSize: '14px',
                                    minWidth: '180px'
                                })
                            }}
                        />
                        {selectedTitle && (
                            <div
                                className={classes.smallDeleteButton}
                                onClick={() => removeFilter('title')}
                            />
                        )}
                    </div>
                    <div className={classes.filterContainer}>
                        <Select
                            value={selectedEvent}
                            onChange={setSelectedEvent}
                            placeholder="Upcoming events"
                            options={upcomingEvents}
                            isCreatable={true}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            menuPlacement={'auto'}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                control: (provided) => ({
                                    ...provided,
                                    borderColor: '#edecec',
                                    borderRadius: '0.75em',
                                    fontSize: '14px',
                                    minWidth: '180px'
                                })
                            }}
                        />
                        {selectedEvent && (
                            <div
                                className={classes.smallDeleteButton}
                                onClick={() => removeFilter('event')}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className={classes.mainContent}>
                <div className={`${classes.interactionSlider} ${isMobileVideoVisible ? classes.showVideo : ''}`}>
                    <div className={`${classes.attendeesSection}`}>
                        <div className={`${classes.header} ${(selectedEvent || selectedTitle) ? classes.biggerTopPadding : classes.smallerTopPadding}`}>Explore the community</div>
                        <div className={`${classes.scrollableCardContainer}`}>
                            {filteredUsers.map((entity, index) => {
                                if (entity.isPlaceholder) {
                                    return (
                                        <div key={entity.userId} className={classes.noVideosCard}>
                                            {isMemberOfCommunity ? (
                                                selectedEvent ? (
                                                    isUserInEventAttendance ? (
                                                        <>
                                                            <div className={classes.noVideosCardContent}>
                                                                <div>Do you want to leave this event?</div>
                                                            </div>
                                                            <Button onClick={() => joinLeaveEvent('leave')} className={classes.leaveButton}>Regrettably leave</Button>
                                                        </>
                                                    ) : 
                                                        (<>
                                                            <div className={classes.noVideosCardContent}>
                                                                <div>Do you want to attend this event?</div>
                                                            </div>
                                                            <Button onClick={() => joinLeaveEvent('join')} className={classes.joinButton}>Happily join</Button>
                                                        </>
                                                    )
                                                ) : (
                                                    <>
                                                        <div className={classes.noVideosCardContent}>
                                                            <div>Attending any upcoming events?</div>
                                                        </div>
                                                        <Button onClick={() => navigate('/profile/' + userMetadata.id + '/edit')}>Join events in your profile</Button>
                                                    </>
                                                )
                                            ) : (
                                                <>
                                                    <div className={classes.noVideosCardContent}>
                                                        <div>People find you within communities.</div>
                                                    </div>
                                                    <Button onClick={() => joinLeaveComm('join')}>Join {currentCommunity.label}</Button>
                                                </>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <SmallPersonCard
                                        key={entity.userId}
                                        userId={entity.userId}
                                        avatar={entity.media.userAvatar}
                                        displayName={entity.profileMetadata?.displayName}
                                        title={entity.profileMetadata?.title}
                                        skill={entity.profileMetadata?.skills ? entity.profileMetadata?.skills[0].display_name : ''}
                                        expertise={entity.profileMetadata?.expertise ? entity.profileMetadata?.expertise[0].display_name : ''}
                                        goal={entity.profileMetadata?.goal}
                                        introduction={entity.profileMetadata?.introduction}
                                        userVideo={entity.media.videoLink}
                                        isSelected={index === selectedCardIndex}
                                        onSelect={() => handleCardSelect(index)}
                                        onMessage={() => messagePerson({ userId: entity?.userId, displayName: entity.profileMetadata?.displayName })}
                                        onShare={() => shareVideo({ userId: entity?.userId, displayName: entity.profileMetadata?.displayName })}
                                    />
                                );
                            })}
                            {
                                filteredUsers.length === 0 && (
                                    <div className={classes.noVideosCard}>
                                        <div className={classes.noVideosCardContent}>
                                            <div>Sorry, couldn't find others here.</div>
                                            <div>Join a community/event in your profile!</div>
                                        </div>
                                        <Button onClick={handleProfileClick}>Go to profile</Button>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                    <div className={classes.videoSection}>
                        {filteredUsers.length === 0 ? (
                            <div className={classes.noVideos}>
                                <Telescope />
                                Looking for more pitches? <br />
                                We'll email you when more are available.
                                <div className={classes.bottomSection}>
                                    <div>
                                        Have you posted <u>your</u> pitch yet?
                                    </div>
                                    <Button onClick={handleCreateClick}>Post It!</Button>
                                </div>
                            </div>
                        ) : (
                            <div className={classes.interactionContainer}>
                                <div
                                    ref={containerRef}
                                    className={classes.videoContainer}
                                >
                                    {filteredUsers?.map((entity, index) => {
                                        if (!entity.isPlaceholder && !entity.userId) return null; // Skip videos with null/undefined id
                                        return (
                                            <div
                                                key={entity.userId}
                                                className={classes.videoWrapper}
                                                ref={(el) => (videoRefs.current[index] = el)}
                                                data-index={index}
                                            >
                                                {!entity.isPlaceholder ? (
                                                    <UserProfileVideoOverlayed
                                                        userId={entity.userId}
                                                        pitchVideo={entity.media.videoLink}
                                                        pitchMetadata={entity.profileMetadata}
                                                        isPlaying={false} // Pass playing state
                                                    />
                                                ) : (
                                                    <div className={classes.noVideos}>
                                                        <Telescope />
                                                        Looking for more pitches? <br />
                                                        We'll email you when more are available.
                                                        <div className={classes.bottomSection}>
                                                            <div>
                                                                Have you posted <u>your</u> pitch yet?
                                                            </div>
                                                            <Button onClick={handleCreateClick}>Post It!</Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className={classes.videoNavButtons}>
                                    <button onClick={() => messagePerson({ userId: filteredUsers[selectedCardIndex]?.userId, displayName: filteredUsers[selectedCardIndex]?.profileMetadata?.displayName })}><Mail01 /></button>
                                    <button onClick={() => shareVideo({ userId: filteredUsers[selectedCardIndex]?.userId, displayName: filteredUsers[selectedCardIndex]?.profileMetadata?.displayName })}><Share06 /></button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Notification for link copied */}
            {notificationVisible && (
                <div className={classes.notification}>
                    {notificationMessage ? notificationMessage : 'Link copied to clipboard!'}
                </div>
            )}
        </div>
    );
}

export default DiscoveryPage;
